import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import PageLayout from '../../components/layout/PageLayout';
import { authApi } from '../../api/auth';
import { useRequestScope } from '../settings/shared';
import { useUserStore } from '../../stores/userStore';
import { useSignupStyle } from '../signup/shared';
import { useSignupDraft } from '../signup/draft';
import { acceptLogin } from './session';
import { authReturnPath, validateLoginUser } from './model';
import {
  appleWebEnabled,
  readAppleFlow,
  clearAppleFlow,
  appleFlowCurrent,
  appleCallbackInput,
} from './appleWeb';

export default function AppleWebReturn() {
  const location = useLocation();
  const user = useUserStore((state) => state.user);
  return <Callback key={location.key} user={user} />;
}
function Callback({ user }) {
  const location = useLocation(),
    nav = useNavigate(),
    { style, t } = useSignupStyle(),
    request = useRequestScope(user);
  const [input] = useState(() => ({
    ...appleCallbackInput(location.search),
    flow: readAppleFlow(),
  }));
  const [error, setError] = useState(null);
  const target = authReturnPath(input.flow?.returnPath);
  useLayoutEffect(() => {
    if (window.location.pathname === location.pathname)
      window.history.replaceState(window.history.state, '', location.pathname);
  }, [location.pathname]);
  useEffect(() => {
    let active = true;
    const r = request();
    // Defer one microtask so StrictMode's setup/cleanup replay cannot consume a ticket twice.
    Promise.resolve().then(async () => {
      if (!active) return;
      clearAppleFlow(input.flow);
      if (!appleWebEnabled() || !input.ticket || !appleFlowCurrent(input.flow)) {
        setError(input.cancelled ? 'auth.socialCancelled' : 'auth.socialLoginFailed');
        return;
      }
      try {
        const result = await authApi.completeAppleWeb(
          { ticket: input.ticket, tabKey: input.flow.tabKey },
          r.options,
        );
        if (!active || !r.current() || !appleFlowCurrent(input.flow)) {
          if (active) setError('auth.socialLoginFailed');
          return;
        }
        const data = result?.data;
        const destination = authReturnPath(data?.returnPath);
        if (data?.user?.isActive === false) {
          setError('auth.inactiveAccount');
          return;
        }
        if (data?.user && !data.signupToken) {
          const accepted = validateLoginUser(data.user, t);
          acceptLogin(accepted);
          nav(destination, { replace: true });
        } else if (
          !data?.user &&
          typeof data?.signupToken === 'string' &&
          data.signupToken.trim()
        ) {
          useSignupDraft.getState().setSocialToken(data.signupToken);
          nav('/signup/detail?redirectUrl=' + encodeURIComponent(destination), { replace: true });
        } else setError('auth.socialLoginFailed');
      } catch {
        if (active) setError('auth.socialLoginFailed');
      }
    });
    return () => {
      active = false;
      r.cancel();
    };
    // A mounted callback owns exactly one ticket; locale changes cannot replay it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <PageLayout paddingTop={0} paddingBottom={0}>
      <div className="pp-settings pp-password pp-signup" style={style}>
        <header className="pp-password-reset-header">
          <h1>{t('auth.login')}</h1>
        </header>
        {error ? (
          <>
            <p role="alert" className="pp-password-error">
              {t(error)}
            </p>
            <Link
              className="pp-password-submit pp-signup-return"
              to={'/login?redirectUrl=' + encodeURIComponent(target)}
              replace
            >
              {t('auth.login')}
            </Link>
            <Link to={target} replace>
              {t('common.back')}
            </Link>
          </>
        ) : (
          <p role="status">{t('auth.loggingIn')}</p>
        )}
      </div>
    </PageLayout>
  );
}
